/**
 * Client-side ads / analytics tracker.
 * Loads GTM, GA4, Meta Pixel, Google Ads, TikTok, LinkedIn.
 *
 * Meta Pixel is installed as soon as the ID is known (so Facebook can detect it),
 * using Meta's Consent API until marketing cookies are accepted.
 * Other marketing tags still wait for consent.
 */

import { hasAcceptedMarketingConsent } from "./trackingConsent";

let config = {
  google_analytics_id: "",
  google_tag_manager_id: "",
  facebook_pixel_id: "",
  google_ads_id: "",
  google_ads_signup_label: "",
  google_ads_purchase_label: "",
  tiktok_pixel_id: "",
  linkedin_partner_id: "",
};

/** Track which tags already injected so late-arriving config still loads. */
const loaded = {
  gtm: "",
  gtagBase: false,
  meta: "",
  tiktok: "",
  linkedin: "",
};

function trimId(v) {
  return String(v || "")
    .trim()
    .replace(/^id[=:\s]+/i, "");
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function gtag() {
  ensureDataLayer();
  window.dataLayer.push(arguments);
}

function injectScript(src, attrs = {}) {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function injectInline(id, code) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.text = code;
  document.head.appendChild(s);
}

function loadGtm(id) {
  if (!id || loaded.gtm === id) return;
  ensureDataLayer();
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`);
  if (!document.getElementById("beyundai-gtm-noscript")) {
    const ns = document.createElement("noscript");
    ns.id = "beyundai-gtm-noscript";
    ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.prepend(ns);
  }
  loaded.gtm = id;
}

function loadGtagBase(measurementIds) {
  const ids = measurementIds.filter(Boolean);
  if (!ids.length || loaded.gtagBase) return;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ids[0])}`);
  window.gtag = window.gtag || gtag;
  gtag("js", new Date());
  ids.forEach((id) => gtag("config", id, { send_page_view: false }));
  loaded.gtagBase = true;
}

/**
 * Install Meta Pixel immediately when ID is set.
 * Uses Consent API so Facebook can detect the base code; full events after Accept.
 */
function loadMeta(pixelId) {
  if (!pixelId) return;
  const id = pixelId.replace(/'/g, "").replace(/[^\d]/g, "");
  if (!id) return;

  if (!window.fbq) {
    injectInline(
      "beyundai-meta-pixel",
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');`,
    );
  }

  if (typeof window.fbq !== "function") return;

  const consented = hasAcceptedMarketingConsent();
  // Consent must be set before the first init for Meta's Consent API.
  window.fbq("consent", consented ? "grant" : "revoke");

  if (loaded.meta !== id) {
    window.fbq("init", id);
    loaded.meta = id;
  }

  if (consented && !document.getElementById("beyundai-meta-noscript")) {
    const ns = document.createElement("noscript");
    ns.id = "beyundai-meta-noscript";
    ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${encodeURIComponent(id)}&ev=PageView&noscript=1" alt="" />`;
    document.body.appendChild(ns);
  }
}

function loadTikTok(pixelId) {
  if (!pixelId || loaded.tiktok === pixelId) return;
  if (window.ttq) {
    loaded.tiktok = pixelId;
    return;
  }
  injectInline(
    "beyundai-tiktok-pixel",
    `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"];
ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;
ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};
n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src=r+"?sdkid="+e+"&lib="+t;
e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
ttq.load('${pixelId.replace(/'/g, "")}');ttq.page();}(window,document,'ttq');`,
  );
  loaded.tiktok = pixelId;
}

function loadLinkedIn(partnerId) {
  if (!partnerId || loaded.linkedin === partnerId) return;
  window._linkedin_partner_id = partnerId;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  if (!window._linkedin_data_partner_ids.includes(partnerId)) {
    window._linkedin_data_partner_ids.push(partnerId);
  }
  if (!window.lintrk) {
    injectInline(
      "beyundai-linkedin-insight",
      `window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[];`,
    );
    injectScript("https://snap.licdn.com/li.lms-analytics/insight.min.js");
  }
  loaded.linkedin = partnerId;
}

export function setTrackingConfig(web = {}) {
  config = {
    google_analytics_id: trimId(web.google_analytics_id),
    google_tag_manager_id: trimId(web.google_tag_manager_id),
    facebook_pixel_id: trimId(web.facebook_pixel_id),
    google_ads_id: trimId(web.google_ads_id),
    google_ads_signup_label: trimId(web.google_ads_signup_label),
    google_ads_purchase_label: trimId(web.google_ads_purchase_label),
    tiktok_pixel_id: trimId(web.tiktok_pixel_id),
    linkedin_partner_id: trimId(web.linkedin_partner_id),
  };
  // Meta installs immediately (consent revoke until Accept). Others need consent.
  ensureScriptsLoaded();
}

export function getTrackingConfig() {
  return { ...config };
}

/**
 * @returns {boolean} true when marketing consent is granted and conversion tags may fire
 */
export function ensureScriptsLoaded() {
  // Always try Meta so Facebook Events Manager / Pixel Helper can find the base code.
  loadMeta(config.facebook_pixel_id);

  if (!hasAcceptedMarketingConsent()) return false;

  const {
    google_tag_manager_id,
    google_analytics_id,
    google_ads_id,
    tiktok_pixel_id,
    linkedin_partner_id,
  } = config;

  loadGtm(google_tag_manager_id);
  loadGtagBase([google_analytics_id, google_ads_id].filter(Boolean));
  loadTikTok(tiktok_pixel_id);
  loadLinkedIn(linkedin_partner_id);

  return true;
}

function pushDataLayer(payload) {
  ensureDataLayer();
  window.dataLayer.push(payload);
}

function fireGoogleAdsConversion(label, params = {}) {
  const adsId = config.google_ads_id;
  if (!adsId || !label || typeof window.gtag !== "function") return;
  window.gtag("event", "conversion", {
    send_to: `${adsId}/${label}`,
    ...params,
  });
}

function canFireMarketingEvents() {
  return hasAcceptedMarketingConsent() && ensureScriptsLoaded();
}

/** Standard page view across enabled pixels. */
export function trackPageView(path) {
  if (!canFireMarketingEvents()) return;
  const page = path || window.location.pathname + window.location.search;

  pushDataLayer({ event: "page_view", page_path: page });

  if (typeof window.gtag === "function") {
    const ids = [config.google_analytics_id, config.google_ads_id].filter(Boolean);
    ids.forEach((id) =>
      window.gtag("config", id, { page_path: page, send_page_view: true }),
    );
    window.gtag("event", "page_view", { page_path: page });
  }
  if (typeof window.fbq === "function") window.fbq("track", "PageView");
  if (window.ttq && typeof window.ttq.page === "function") window.ttq.page();
}

/** CompleteRegistration / sign_up */
export function trackSignUp(extra = {}) {
  if (!canFireMarketingEvents()) return;
  pushDataLayer({ event: "sign_up", method: extra.method || "email", ...extra });

  if (typeof window.gtag === "function") {
    window.gtag("event", "sign_up", { method: extra.method || "email" });
  }
  if (typeof window.fbq === "function") {
    window.fbq("track", "CompleteRegistration", extra);
  }
  if (window.ttq && typeof window.ttq.track === "function") {
    window.ttq.track("CompleteRegistration");
  }
  fireGoogleAdsConversion(config.google_ads_signup_label);
}

/** Purchase / paid plan activation */
export function trackPurchase({
  value = 0,
  currency = "USD",
  transaction_id,
  items,
} = {}) {
  if (!canFireMarketingEvents()) return;
  const payload = {
    event: "purchase",
    value: Number(value) || 0,
    currency: currency || "USD",
    transaction_id: transaction_id || undefined,
    items: items || undefined,
  };
  pushDataLayer(payload);

  if (typeof window.gtag === "function") {
    window.gtag("event", "purchase", {
      value: payload.value,
      currency: payload.currency,
      transaction_id: payload.transaction_id,
    });
  }
  if (typeof window.fbq === "function") {
    window.fbq("track", "Purchase", {
      value: payload.value,
      currency: payload.currency,
    });
  }
  if (window.ttq && typeof window.ttq.track === "function") {
    window.ttq.track("CompletePayment", {
      value: payload.value,
      currency: payload.currency,
    });
  }
  fireGoogleAdsConversion(config.google_ads_purchase_label, {
    value: payload.value,
    currency: payload.currency,
    transaction_id: payload.transaction_id,
  });
}

export function trackInitiateCheckout({ value = 0, currency = "USD" } = {}) {
  if (!canFireMarketingEvents()) return;
  pushDataLayer({ event: "begin_checkout", value, currency });
  if (typeof window.gtag === "function") {
    window.gtag("event", "begin_checkout", { value, currency });
  }
  if (typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", { value, currency });
  }
  if (window.ttq && typeof window.ttq.track === "function") {
    window.ttq.track("InitiateCheckout", { value, currency });
  }
}

export function trackLead(extra = {}) {
  if (!canFireMarketingEvents()) return;
  pushDataLayer({ event: "generate_lead", ...extra });
  if (typeof window.gtag === "function") window.gtag("event", "generate_lead", extra);
  if (typeof window.fbq === "function") window.fbq("track", "Lead", extra);
  if (window.ttq && typeof window.ttq.track === "function") {
    window.ttq.track("SubmitForm");
  }
}

export function trackCustomEvent(name, params = {}) {
  if (!canFireMarketingEvents() || !name) return;
  pushDataLayer({ event: name, ...params });
  if (typeof window.gtag === "function") window.gtag("event", name, params);
  if (typeof window.fbq === "function") window.fbq("trackCustom", name, params);
}
